import moment from 'moment';
import React from 'react';
import { useMutation } from '@apollo/client';
import { useAppDispatch } from '../app/redux/useAppSelector';
import { FormConfirmationModal } from '../components/ConfirmationModal';
import { Field } from '../components/Form';
import Timestamp from '../components/Timestamp';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { postToasts } from '../toasts/toasts';
import { UIActions } from '../ui/uiSlice';
import yearSensitiveDateString from '../utils/yearSensitiveDateString';
import VerifiedIcon from './VerifiedIcon';
import getVerificationStatus, {
  VerificationStatus,
} from './getVerificationStatus';
import {
  calculateExpirationTimestamp,
  humanReadableFrequency,
} from './verificationDateHelpers';

const POST_FRAGMENT = gql(`
  fragment VerifyPostModal_post on Post {
    id
    verification {
      id
      unit
      quantity
    }
    verificationEntries {
      id
      verifiedAt
    }
    ...getVerificationStatus_post
  }
`);

const VERIFY_POST_MUTATION = gql(/** @complexity 12 */ `
  mutation verifyPost($verificationId: ID!) {
    verificationEntry: verifyPost(verificationId: $verificationId) {
      id
      post {
        id
        verificationEntries {
          id
          verifiedAt
          expiredAt
          verifier {
            id
          }
        }
      }
    }
  }
`);

interface VerifyPostModalProps {
  post: DocumentType<typeof POST_FRAGMENT>;
  close: () => void;
  onVerify?: () => void;
  noChanges?: boolean;
}

type FormValues = { confirm: boolean };

const VerifyPostModal = ({
  post,
  close,
  onVerify,
  noChanges,
}: VerifyPostModalProps) => {
  const dispatch = useAppDispatch();
  const newExpirationDateString = post.verification
    ? yearSensitiveDateString(
        calculateExpirationTimestamp(
          moment().valueOf(),
          post.verification,
        ).unsafe(),
      )
    : '';
  const frequencyString = post.verification
    ? humanReadableFrequency(post.verification)
    : '';

  let description: React.ReactNode | undefined;
  const status = getVerificationStatus(post);
  if (status === VerificationStatus.Requested) {
    description = `Verification request is pending. Confirm its accuracy to verify for ${frequencyString}, until ${newExpirationDateString}.`;
  } else if (status === VerificationStatus.Expired) {
    if (noChanges) {
      description = `Verification has expired, though no changes have been made to the post. Confirm its accuracy to verify for ${frequencyString}, until ${newExpirationDateString}.`;
    } else {
      description = `Verification has expired. Confirm its accuracy to verify for ${frequencyString}, until ${newExpirationDateString}.`;
    }
  } else if (post.verification) {
    const expirationTimestamp = calculateExpirationTimestamp(
      post.verificationEntries[0]?.verifiedAt,
      post.verification,
    ).unsafe();

    description = (
      <>
        Verification expires{' '}
        <Timestamp date={expirationTimestamp} format="future" />. Confirm its
        accuracy to keep its badge for {frequencyString}, until{' '}
        {newExpirationDateString}.
      </>
    );
  }

  const [verifyPost] = useMutation(VERIFY_POST_MUTATION);
  const onSubmit = async () => {
    const { verification } = post;
    if (!verification) {
      return null;
    }
    return verifyPost({
      variables: { verificationId: verification.id },
    }).then(() => {
      close();
      onVerify?.();
      dispatch(
        UIActions.pushToast(
          postToasts.verified({
            quantity: verification.quantity,
            unit: verification.unit,
          }),
        ),
      );
    });
  };

  return (
    <FormConfirmationModal<FormValues>
      title={
        status === VerificationStatus.Requested
          ? 'Verify post'
          : 'Reverify post'
      }
      titleExtra={<VerifiedIcon />}
      description={description}
      width={515}
      close={close}
      form={{
        onSubmit,
        initialValues: {
          confirm: false,
        },
      }}
      submitButton={{
        text: 'Verify',
      }}
    >
      <Field
        name="confirm"
        label="I've reviewed this post for accuracy"
        type="checkbox"
        required
      />
    </FormConfirmationModal>
  );
};

export default VerifyPostModal;
